import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClickOutside } from '../lib/useClickOutside'
import { TOPBAR_BTN_CLASS } from '../app/topbarButton'
import { useNotifications } from '../hooks/useNotifications'
import { MarkAllReadButton } from './MarkAllReadButton'
import { NotificationGroup } from './NotificationGroup'
import { NotificationsEmpty } from './NotificationsList'

type GroupKey = 'account' | 'user'

/** The top-bar bell and its dropdown — the prototype's `#notif-btn` /
 * `#notif-menu` (MS-868, ARCHITECTURE_DECISIONS.md §49). The dot shows while
 * anything is unread. The dropdown is the only notification surface (Home
 * lost its notification centre, MS-996 / ARCHITECTURE_DECISIONS.md §53): a
 * header with the "Notifications" title and Mark all as read, then the two
 * categories (account level first), each collapsible; only the list scrolls,
 * capped at 80% of the window less the header. */
export function NotificationsMenu() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  // Held here, not in the group, so a dismiss or refetch never reopens a
  // category the reader closed (prototype `notifGroupCollapsed`).
  const [collapsed, setCollapsed] = useState<Record<GroupKey, boolean>>({ account: false, user: false })
  const containerRef = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setOpen(false), [])
  useClickOutside(containerRef, close, open)
  const notifications = useNotifications()
  const { account, user, unreadCount } = notifications
  const toggleGroup = (key: GroupKey) => setCollapsed((c) => ({ ...c, [key]: !c[key] }))

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`relative ${TOPBAR_BTN_CLASS}`}
        title={t('appShell.notifications')}
        aria-label={t('appShell.notifications')}
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span
            data-testid="notif-unread-dot"
            className="absolute top-[5px] right-[5px] w-[9px] h-[9px] rounded-full bg-teal-300 border-2"
            style={{ borderColor: 'var(--header-bg)' }}
          />
        )}
      </button>

      {open && (
        <div
          data-testid="notif-menu"
          className="absolute right-0 top-[calc(100%+8px)] w-[380px] overflow-hidden bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] z-[60] text-[var(--text-primary)]"
          // Inline: Tailwind reads `shadow-[var(--…)]` as a shadow colour, not
          // a shadow, and would paint none (the prototype's `.topbar-menu`
          // has `box-shadow: var(--shadow-lg)`).
          style={{ boxShadow: 'var(--shadow-lg)' }}
        >
          <div className="flex items-center justify-between gap-2.5 px-3.5 py-3 border-b border-[var(--border-light)] text-[13px] font-semibold">
            <span>{t('appShell.notifications')}</span>
            <MarkAllReadButton unreadCount={unreadCount} onMarkAll={notifications.markAllRead} />
          </div>
          <div data-testid="notif-list" className="max-h-[calc(80vh-46px)] overflow-y-auto">
            {account.length + user.length === 0 ? (
              <NotificationsEmpty />
            ) : (
              <>
                <NotificationGroup
                  label={t('notifications.groupAccount')}
                  items={account}
                  collapsed={collapsed.account}
                  onToggle={() => toggleGroup('account')}
                  api={notifications}
                  onNavigate={close}
                />
                <NotificationGroup
                  label={t('notifications.groupUser')}
                  items={user}
                  collapsed={collapsed.user}
                  onToggle={() => toggleGroup('user')}
                  api={notifications}
                  onNavigate={close}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
